import { useAssignSessionsMentor } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import Modal from '@components/V4/Modal/Modal';
import Select from '@components/V4/Select/Select';
import { useDebounce } from '@hooks';
import { useGetAllMentors } from 'apps/agora/src/actions/users.actions';
import useToast from 'apps/agora/src/hooks/useToast';
import { useState } from 'react';

const PRIMARY_MENTOR_VALUE = 'primary';

interface AssignSessionsMentorModalProps {
  isOpen: boolean;
  enrollmentId: string;
  sessions: { _id: string; index: number }[];
  primaryMentorName?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const AssignSessionsMentorModal = (props: AssignSessionsMentorModalProps) => {
  const { isOpen, enrollmentId, sessions, primaryMentorName, onClose, onSuccess } = props;

  const toast = useToast();

  const [mentorValue, setMentorValue] = useState<string>();
  const [mentorSearch, setMentorSearch] = useState('');
  const debouncedMentorSearch = useDebounce(mentorSearch, 500);

  const { data: mentorsData, isLoading: mentorsLoading } = useGetAllMentors({
    search: debouncedMentorSearch,
    active: true,
  });

  const { mutate: assignSessionsMentor, isLoading: isAssigning } = useAssignSessionsMentor({
    onSuccess() {
      toast.success(
        `Successfully assigned ${sessions.length} ${
          sessions.length === 1 ? 'session' : 'sessions'
        }.`
      );
      onSuccess();
      handleClose();
    },
    onError(error: any) {
      toast.error(
        error?.response?.data?.error?.message ?? 'Sessions could not be assigned. Please try again.'
      );
    },
  });

  const mentorOptions = [
    {
      label: primaryMentorName ? `Primary mentor (${primaryMentorName})` : 'Primary mentor',
      value: PRIMARY_MENTOR_VALUE,
    },
    ...(mentorsData?.map((mentor) => ({ label: mentor.fullName, value: mentor._id })) ?? []),
  ];

  const handleClose = () => {
    setMentorValue(undefined);
    setMentorSearch('');
    onClose();
  };

  const handleSubmit = () => {
    if (!mentorValue) return;

    assignSessionsMentor({
      enrollmentId,
      sessionIds: sessions.map((session) => session._id),
      mentorId: mentorValue === PRIMARY_MENTOR_VALUE ? null : mentorValue,
    });
  };

  const sortedIndexes = sessions.map((session) => session.index).sort((a, b) => a - b);
  const sessionLabel = sortedIndexes.map((index) => index + 1).join(', ');

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="small">
      <Modal.Header title="Assign mentor to sessions" />
      <Modal.Body className="flex flex-col gap-4">
        <p className="text-xs text-customGrey">
          {sortedIndexes.length === 1
            ? `Session ${sessionLabel} will be held by:`
            : `Sessions ${sessionLabel} will be held by:`}
        </p>

        <Select
          label="Mentor"
          value={mentorValue}
          filterOption={() => true}
          searchValue={mentorSearch}
          onSearch={setMentorSearch}
          onSelect={(value) => setMentorValue(value as string)}
          options={mentorOptions}
          notFoundContent={
            mentorsLoading ? <LoadingSpinner className="mx-auto" size="normal" /> : null
          }
          placeholder="Search Mentor"
          variant="primary"
          isLoading={mentorsLoading}
          isRequired
        />

        <p className="text-xs text-customGrey">
          Picking the primary mentor clears the assignment and hands these sessions back to the
          enrollment mentor.
        </p>

        <div className="flex justify-end gap-3">
          <Button variant="secondary" buttonText="Cancel" onClick={handleClose} />
          <Button
            variant="primary"
            buttonText="Assign"
            disabled={!mentorValue}
            isLoading={isAssigning}
            onClick={handleSubmit}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AssignSessionsMentorModal;
